import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { ArrowUpRight, Warning, X } from '@phosphor-icons/react';
import Button from '../../../../components/Button';
import { captureAnalytics } from '../../../../actions/preload';
import {
  MAX_NEW_TABS, isOpenableRecord, openableDocuments, openPathsInNewTabs, showsOpenActions,
} from '../../../../util/recordOpenActions';
import './openActionButtons.scss';

// ENG-3676 (PR 2): the toolbar's way to open the selection. One record opens in the
// side panel or the full page; several open as full pages, one tab each. There is
// no side-panel option for several, since stepping through a selection there felt
// wrong in review.
// The blocked-tabs notice belongs to the selection that produced it. Any change to
// the selection, including clearing it, drops the notice, so its Try again can never
// open rows that are no longer selected.
const selectionKey = (records) => records.map((record) => (
  record && `${record.file_token}|${record.send_token}|${record.slug}`
)).join(',');

const OpenActionButtons = ({ records, recordOpenActions, trackActionClick }) => {
  const [storedNotice, setNotice] = useState(null);
  const currentKey = selectionKey(records);
  if (storedNotice && storedNotice.key !== currentKey) setNotice(null);
  if (!showsOpenActions(records, recordOpenActions)) return null;

  const { inbox } = recordOpenActions;
  const notice = storedNotice && storedNotice.key === currentKey ? storedNotice : null;
  const openable = openableDocuments(records);
  const track = (action) => trackActionClick(action, 'primary');
  // Rows left out because they have no document, or open message history instead.
  const skippedRows = records.filter((record) => record && record !== 'all' && !isOpenableRecord(record)).length;
  const skippedNote = skippedRows > 0
    ? `${skippedRows} selected ${skippedRows === 1 ? 'row has' : 'rows have'} no document to open and ${skippedRows === 1 ? 'is' : 'are'} skipped`
    : null;

  if (openable.length === 1) {
    const [record] = openable;
    return (
      <>
        <Tooltip title={skippedNote} placement="bottom">
          <div>
            <Button
              type="primary"
              size="xsmall"
              label="Open"
              onClick={() => {
                track('open');
                recordOpenActions.openInSidePanel(record);
              }}
            />
          </div>
        </Tooltip>
        <Tooltip title={skippedNote} placement="bottom">
          <div>
            <Button
              type="secondary"
              size="xsmall"
              label="Full view"
              rightIcon={<ArrowUpRight size={14} weight="bold" />}
              onClick={() => {
                track('full_view');
                recordOpenActions.openFullView(record);
              }}
            />
          </div>
        </Tooltip>
      </>
    );
  }

  const tooMany = openable.length > MAX_NEW_TABS;
  const reason = tooMany ? `Select ${MAX_NEW_TABS} or fewer documents to open them in new tabs` : skippedNote;

  const openTabs = (paths, total) => {
    const blocked = openPathsInNewTabs(paths);
    if (blocked.length === 0) {
      setNotice(null);
      return;
    }
    captureAnalytics('record_open_tabs_blocked', { inbox, blocked: blocked.length, total });
    setNotice({
      key: currentKey, opened: total - blocked.length, total, blocked,
    });
  };

  return (
    <>
      <Tooltip title={reason} placement="bottom">
        <div>
          <Button
            type="primary"
            size="xsmall"
            label={`Open in new tabs (${openable.length})`}
            rightIcon={<ArrowUpRight size={14} weight="bold" />}
            disabled={tooMany}
            onClick={() => {
              track('open_in_new_tabs');
              openTabs(openable.map(recordOpenActions.fullViewPath), openable.length);
            }}
          />
        </div>
      </Tooltip>
      {notice && (
        <div className="open-tabs-notice" role="alert">
          <Warning className="open-tabs-notice__icon" size={20} weight="fill" aria-hidden="true" />
          <div className="open-tabs-notice__text">
            <div className="open-tabs-notice__title">
              {`Opened ${notice.opened} of ${notice.total}. Your browser blocked the other ${notice.blocked.length} ${notice.blocked.length === 1 ? 'tab' : 'tabs'}.`}
            </div>
            <div>
              Allow pop-ups for this site in your browser, then try again. You only have to do
              this once. In Chrome, click the blocked pop-up icon at the right end of the address
              bar and choose
              {' '}
              <strong>Always allow pop-ups and redirects</strong>
              .
            </div>
          </div>
          <button
            type="button"
            className="open-tabs-notice__retry"
            onClick={() => {
              track('open_in_new_tabs_retry');
              const blocked = openPathsInNewTabs(notice.blocked);
              if (blocked.length === 0) setNotice(null);
              else setNotice({ ...notice, opened: notice.opened + (notice.blocked.length - blocked.length), blocked });
            }}
          >
            Try again
          </button>
          <button
            type="button"
            className="open-tabs-notice__close"
            aria-label="Close"
            onClick={() => {
              track('open_in_new_tabs_notice_closed');
              setNotice(null);
            }}
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      )}
    </>
  );
};

OpenActionButtons.defaultProps = {
  recordOpenActions: null,
};

OpenActionButtons.propTypes = {
  records: PropTypes.array.isRequired,
  recordOpenActions: PropTypes.shape({
    inbox: PropTypes.string,
    openInSidePanel: PropTypes.func.isRequired,
    openFullView: PropTypes.func.isRequired,
    fullViewPath: PropTypes.func.isRequired,
  }),
  trackActionClick: PropTypes.func.isRequired,
};

export default OpenActionButtons;
